import { useScienceTutorSection } from './scienceTutorContext';
import { useMemo, useState } from 'react';

import {
  DISPLACING_HALOGENS,
  HALOGEN_TEMPERATURE_FIELDS,
  HALOGENS,
  checkDisplacementPrediction,
  displacementCell,
  formatFullEquation,
  formatIonicEquation,
  halogenById,
  halogenStatesAt,
  halogenTrendExplanation,
  questionsFor,
  type DisplacementCell,
  type HalogenId,
} from './groupReactivity';
import { ExploreChallenges } from './ExploreChallenges';
import { FeedbackPanel, QuestionList, ShellDiagram, TrendBars } from './GroupReactivityVisuals';
import { DEFAULT_HALOGEN_TEMPERATURE_C, HALOGEN_STATES_CHALLENGES, HALOGEN_STATES_QUESTION } from './halogenStatesExplore';
import { ModelInputPanel } from './ModelInputs';
import { formatFormula } from './reactionPredictionData';

/** Token approximations of solution colours; every swatch is paired with its colour name. */
const SOLUTION_SWATCHES: Record<string, string> = {
  'very pale green': 'bg-wash-mint',
  orange: 'bg-gradient-to-br from-brand-sunshine to-brand-coral',
  brown: 'bg-gradient-to-br from-brand-coral to-ink-soft',
};

function Swatch({ colour }: { colour: string }) {
  return <span className={`inline-block h-4 w-4 shrink-0 rounded-full border border-hairline ${SOLUTION_SWATCHES[colour] ?? 'bg-white'}`} aria-hidden="true" />;
}

function cellLabel(cell: DisplacementCell) {
  return `${halogenById(cell.halogen).name} water with K${cell.halide}`;
}

function DisplacementGrid({
  selectedId,
  revealed,
  onSelect,
}: {
  selectedId: string;
  revealed: Set<string>;
  onSelect: (cell: DisplacementCell) => void;
}) {
  return (
    <table className="w-full table-fixed border-separate border-spacing-1 text-center text-xs sm:text-sm">
      <caption className="sr-only">Halogen displacement grid: rows are halogen water, columns are potassium halide solutions</caption>
      <thead>
        <tr>
          <th scope="col" className="p-1 text-left text-[11px] font-extrabold text-slate2">Add ↓ to →</th>
          {DISPLACING_HALOGENS.map((halide) => (
            <th key={halide} scope="col" className="p-1 font-extrabold text-ink">
              K{halide}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {DISPLACING_HALOGENS.map((halogen) => (
          <tr key={halogen}>
            <th scope="row" className="p-1 text-left font-extrabold text-ink">
              {formatFormula(halogenById(halogen).molecule)}
            </th>
            {DISPLACING_HALOGENS.map((halide) => {
              const cell = displacementCell(halogen, halide);
              if (cell.status === 'same-halogen') {
                return (
                  <td key={halide} className="rounded-xl bg-hairline-soft p-2 text-[11px] font-bold text-slate2">
                    same
                  </td>
                );
              }
              const shown = revealed.has(cell.id);
              return (
                <td key={halide} className="p-0">
                  <button
                    type="button"
                    aria-pressed={selectedId === cell.id}
                    aria-label={cellLabel(cell)}
                    onClick={() => onSelect(cell)}
                    className={`w-full rounded-xl border px-1 py-2 text-[11px] font-extrabold transition-colors motion-reduce:transition-none sm:text-xs ${
                      selectedId === cell.id ? 'border-brand-coral bg-wash-coral text-ink' : 'border-hairline bg-white text-ink-soft hover:border-brand-coral/40'
                    }`}
                  >
                    {shown ? (cell.status === 'reaction' ? '✓ reacts' : '✗ none') : '?'}
                  </button>
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function HalogensMode() {
  const [focusId, setFocusId] = useState<HalogenId>('Cl');
  const [cell, setCell] = useState<DisplacementCell>(() => displacementCell('Cl', 'Br'));
  const [prediction, setPrediction] = useState<'reaction' | 'no-reaction' | ''>('');
  const [revealed, setRevealed] = useState<Set<string>>(() => new Set());
  const [temperature, setTemperature] = useState({ temperatureC: DEFAULT_HALOGEN_TEMPERATURE_C });
  const states = useMemo(() => halogenStatesAt(temperature.temperatureC), [temperature.temperatureC]);
  const challengeInput = useMemo(
    () => ({ temperatureC: temperature.temperatureC, states }),
    [temperature.temperatureC, states],
  );
  const stateOf = (id: HalogenId) => states.find((entry) => entry.id === id);
  useScienceTutorSection('HalogensMode', { focusId, cell, prediction, revealed, temperatureC: temperature.temperatureC });
  const focus = halogenById(focusId);
  const result = revealed.has(cell.id) && prediction ? checkDisplacementPrediction(cell, prediction === 'reaction') : null;
  const ionic = formatIonicEquation(cell);
  const full = formatFullEquation(cell);
  const halogen = halogenById(cell.halogen);

  const selectCell = (next: DisplacementCell) => {
    setCell(next);
    setPrediction('');
  };

  return (
    <div className="space-y-6">
      <section aria-label="Physical trend" className="rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">
          Physical trend at {temperature.temperatureC} °C
        </p>
        <div className="mt-3">
          <ModelInputPanel
            legend="Choose a temperature (100 kPa)"
            fields={HALOGEN_TEMPERATURE_FIELDS}
            values={temperature}
            onChange={setTemperature}
            sliderKey="temperatureC"
          />
        </div>
        <div className="mt-3 grid gap-2 sm:grid-cols-4" role="group" aria-label="Choose a halogen">
          {HALOGENS.map((option) => (
            <button
              key={option.id}
              type="button"
              aria-pressed={focusId === option.id}
              aria-label={option.name}
              onClick={() => setFocusId(option.id)}
              className={`rounded-2xl border p-3 text-left transition-colors motion-reduce:transition-none ${
                focusId === option.id ? 'border-brand-sky bg-wash-sky' : 'border-hairline bg-white hover:border-brand-sky/40'
              }`}
            >
              <span className="block text-lg font-extrabold text-ink">{formatFormula(option.molecule)}</span>
              <span className="block text-xs font-extrabold uppercase tracking-wide text-slate2" data-testid={`halogen-state-${option.id}`}>
                {stateOf(option.id)?.state}
              </span>
              <span className="mt-1 block font-mono text-[11px] font-bold leading-4 text-ink" data-testid={`halogen-state-working-${option.id}`}>
                {stateOf(option.id)?.working}
              </span>
              {temperature.temperatureC === DEFAULT_HALOGEN_TEMPERATURE_C ? (
                <span className="mt-1 block text-xs font-medium leading-5 text-ink-soft">{option.appearance}</span>
              ) : null}
            </button>
          ))}
        </div>
        <div className="mt-3">
          <ExploreChallenges question={HALOGEN_STATES_QUESTION} challenges={HALOGEN_STATES_CHALLENGES} result={challengeInput} />
        </div>
        <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,13rem)_minmax(0,1fr)_minmax(0,1fr)] lg:items-start">
          <ShellDiagram
            symbol={focus.id}
            atomicNumber={focus.shells.reduce((sum, count) => sum + count, 0)}
            shells={focus.shells}
            label={`Shell diagram of ${focus.name}: electron arrangement ${focus.shells.join(', ')}; 7 outer electrons in shell ${focus.shells.length}`}
          />
          <div className="text-sm font-medium leading-6 text-ink-soft">
            <p className="font-extrabold text-ink">
              {focus.name.charAt(0).toUpperCase() + focus.name.slice(1)}: {focus.shells.join(', ')} · melts {focus.meltingPointC} °C · boils{' '}
              {focus.boilingPointC} °C
            </p>
            <p className="mt-1">{halogenTrendExplanation(focus)}</p>
            {focus.id === 'F' ? (
              <p className="mt-1 text-xs font-bold text-slate2">Fluorine is far too reactive and toxic for school experiments.</p>
            ) : null}
          </div>
          <TrendBars
            title="Boiling point"
            unit="°C"
            bars={HALOGENS.map((entry) => ({ id: entry.id, label: entry.id, value: entry.boilingPointC, display: `${entry.boilingPointC} °C`, active: entry.id === focus.id }))}
            note="Bigger molecules, stronger forces between them: gas → liquid → solid."
          />
        </div>
      </section>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
        <section aria-label="Displacement grid" className="min-w-0 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">Displacement: choose a combination</p>
          <p className="mt-1 text-sm font-medium leading-6 text-slate2">
            Each cell adds halogen water to a colourless potassium halide solution.
          </p>
          <div className="mt-3">
            <DisplacementGrid selectedId={cell.id} revealed={revealed} onSelect={selectCell} />
          </div>
        </section>

        <section aria-label="Predict the displacement" className="min-w-0 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
          <h3 className="text-lg font-extrabold text-ink">
            {formatFormula(halogen.molecule)} water + K{cell.halide} solution
          </h3>
          <p className="mt-1 flex items-center gap-2 text-sm font-medium text-slate2">
            <Swatch colour={cell.startColour} /> Start: {cell.startColour} {halogen.name} water, colourless K{cell.halide}
          </p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2" role="group" aria-label="Predict the result">
            {(['reaction', 'no-reaction'] as const).map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={prediction === option}
                onClick={() => {
                  setPrediction(option);
                  setRevealed((current) => {
                    const next = new Set(current);
                    next.delete(cell.id);
                    return next;
                  });
                }}
                className={`rounded-2xl border px-4 py-2.5 text-left text-sm font-extrabold transition-colors motion-reduce:transition-none ${
                  prediction === option ? 'border-brand-coral bg-wash-coral text-ink' : 'border-hairline bg-white text-ink-soft hover:border-brand-coral/40'
                }`}
              >
                {option === 'reaction' ? 'Reaction: the colour changes' : 'No reaction: no change'}
              </button>
            ))}
          </div>
          <button
            type="button"
            disabled={!prediction}
            onClick={() => setRevealed((current) => new Set(current).add(cell.id))}
            className="mt-3 rounded-full bg-ink px-5 py-2.5 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            Mix and check
          </button>

          {result ? (
            <div className="mt-4 space-y-3">
              <FeedbackPanel label="Displacement feedback" result={result} />
              <p className="flex items-center gap-2 text-sm font-extrabold text-ink">
                <Swatch colour={cell.endColour} /> End colour: {cell.endColour}
                {cell.status === 'reaction' ? ` (${halogenById(cell.halide).name} formed)` : ' (unchanged)'}
              </p>
              {ionic && full ? (
                <div className="rounded-2xl border border-hairline bg-white p-4">
                  <p className="text-xs font-extrabold uppercase tracking-wide text-slate2">Full equation</p>
                  <p className="mt-1 break-words text-sm font-extrabold text-ink">{full}</p>
                  <p className="mt-3 text-xs font-extrabold uppercase tracking-wide text-slate2">Ionic equation (K⁺ is a spectator)</p>
                  <p className="mt-1 break-words text-base font-extrabold text-ink" aria-label="Ionic equation">
                    {ionic}
                  </p>
                </div>
              ) : null}
            </div>
          ) : null}
        </section>
      </div>

      <QuestionList title="Halogen predictions" questions={questionsFor('halogens')} />
    </div>
  );
}
